import { StanButton } from '@/components';
import { useStanRouter } from '@/hooks/useStanRouter';
import { connectInstagram } from '@/lib/utils/instagramConnect';
import { createIconElement } from '@/lib';

import AutomationEmptyImage from '@/assets/images/automations/hero-autoreply.svg';
import InstagramIcon from '@/assets/icons/ic-instagram.svg';
import IcPlus from '@/assets/icons/ic-plus.svg';

interface InitialEmptyStateProps {
    instagramStatus: 'connected' | 'not-connected' | 'loading';
    hasActiveSubscription: boolean;
    onCreateAutomation: () => void;
    onInstagramConnected?: () => void;
}

/**
 * Initial empty state for Automations page
 * Shows when user has no automations yet
 * Prompts to either renew subscription, connect Instagram, or create first automation
 * Vue implementation: /src/pages/automations/Automations.vue (empty-landing-page section)
 */
export const InitialEmptyState = ({
    instagramStatus,
    hasActiveSubscription,
    onCreateAutomation,
    onInstagramConnected,
}: InitialEmptyStateProps) => {
    const { push } = useStanRouter();

    const isConnected = instagramStatus === 'connected';
    const isLoading = instagramStatus === 'loading';

    const handleRenewSubscription = () => {
        push('/subscribe/plan');
    };

    const handleConnectInstagram = () => {
        connectInstagram(() => {
            onInstagramConnected?.();
        });
    };

    return (
        <div className='mx-auto flex w-full max-w-screen-xl flex-col items-start px-2'>
            <div className='mt-[5%] flex w-full flex-col gap-6 sm:gap-8 sm:px-4 md:gap-12 md:px-8 lg:px-12 xl:px-14'>
                <p className='text-semantic-fg-base text-3xl font-extrabold md:text-5xl'>
                    Create an <br />
                    IG Auto-Reply
                </p>
                <div className='flex w-full justify-between gap-4 max-md:flex-col lg:gap-6'>
                    <div className='flex w-full flex-col gap-8 pr-1 md:gap-12'>
                        <div className='text-semantic-fg-muted text-sm md:text-base'>
                            <p className='pb-4 md:pb-6 lg:max-w-full'>
                                Set your engagement on autopilot! Just pick a keyword, craft your message, and watch as
                                everyone who comments your magic word gets a personalized response instantly!
                            </p>
                        </div>
                        {!hasActiveSubscription && (
                            <StanButton
                                name='automations-renew-subscription-button'
                                onClick={handleRenewSubscription}
                                icon={createIconElement(IcPlus, { className: 'size-5' })}
                                className='md:text-base! lg:max-w-64'
                                disabled={isLoading}
                                loading={isLoading}
                            >
                                Renew Subscription
                            </StanButton>
                        )}
                        {hasActiveSubscription && !isConnected && (
                            <StanButton
                                name='automations-connect-instagram-button'
                                onClick={handleConnectInstagram}
                                icon={<InstagramIcon className='size-5' />}
                                className='md:text-base! lg:max-w-64'
                                disabled={isLoading}
                                loading={isLoading}
                            >
                                Connect Instagram
                            </StanButton>
                        )}
                        {hasActiveSubscription && isConnected && (
                            <StanButton
                                name='automations-create-auto-reply-button'
                                onClick={onCreateAutomation}
                                icon={createIconElement(IcPlus, { className: 'size-5' })}
                                className='md:text-base! lg:max-w-64'
                                disabled={isLoading}
                                loading={isLoading}
                            >
                                Create Auto-Reply
                            </StanButton>
                        )}
                    </div>
                    <div className='max-md:mx-auto max-md:pt-12 md:w-full lg:-translate-y-12 xl:pr-24'>
                        <AutomationEmptyImage
                            className='pointer-events-none ml-auto w-full max-w-80 object-contain select-none lg:max-w-sm'
                            aria-label='Instagram Automation illustration'
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
